import React, { useState, useEffect, useCallback } from "react";
import cogoToast from "cogo-toast";
import httpRequest from "../../../../../../ajax";
import ShareButton from "../../ShareButton";

/**
 * EditorialArticleView — full article reading experience inside the feed.
 *
 * Per PRD §5.3:
 *   - "Read More" opens the full article inside the feed (in-app reading)
 *   - Canonical tag rendered in HTML head pointing to blog URL
 *   - Comments section under editorial articles (flat, no threading)
 *   - Empty state (PRD §10): "Start the conversation" placeholder
 *
 * Props:
 *   card — the editorial feed card
 *   onBack — callback to return to the feed
 */
const EditorialArticleView = ({ card, onBack }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchArticle = useCallback(async () => {
    try {
      const res = await httpRequest("get", `/feed-cards/editorial/${card.id}`);
      if (res?.data?.status) {
        setArticle(res.data.data);
      }
    } catch (err) {
      console.error("EditorialArticleView: fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [card.id]);

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const res = await httpRequest("get", `/feed-cards/editorial/${card.id}/comments`);
      if (res?.data?.status) {
        setComments(res.data.data);
      }
    } catch (err) {
      console.error("EditorialArticleView: comments fetch error", err);
    } finally {
      setLoadingComments(false);
    }
  }, [card.id]);

  useEffect(() => {
    fetchArticle();
    fetchComments();
  }, [fetchArticle, fetchComments]);

  const handleAddComment = async () => {
    if (!commentBody.trim()) return;
    setPostingComment(true);
    try {
      const res = await httpRequest("post", `/feed-cards/editorial/${card.id}/comments`, {
        body: commentBody.trim(),
      });
      if (res?.data?.status) {
        // Refresh comments and article so count updates
        fetchComments();
        fetchArticle();
        setCommentBody("");
      }
    } catch (err) {
      console.error("EditorialArticleView: add comment error", err);
    } finally {
      setPostingComment(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editBody.trim()) return;
    setSavingEdit(true);
    try {
      const res = await httpRequest("put", `/feed-cards/editorial/${card.id}/comments/${commentId}`, {
        body: editBody.trim(),
      });
      if (res?.data?.status) {
        setComments(comments.map(c =>
          c.comment_id === commentId ? { ...c, body: editBody.trim() } : c
        ));
        setEditingId(null);
        setEditBody("");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to edit comment";
      cogoToast.error(msg, { position: "top-center", hideAfter: 4 });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeletingId(commentId);
    try {
      const res = await httpRequest("delete", `/feed-cards/editorial/${card.id}/comments/${commentId}`);
      if (res?.data?.status) {
        // Refresh comments and article so count updates
        fetchComments();
        fetchArticle();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete comment";
      cogoToast.error(msg, { position: "top-center", hideAfter: 4 });
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.comment_id);
    setEditBody(comment.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div style={styles.errorWrap}>
        <p>Article not found.</p>
        <button style={styles.backBtn} onClick={onBack}>← Back to feed</button>
      </div>
    );
  }

  const categoryColors = {
    "Career Advice":      { bg: "#eff6ff", color: "#1d4ed8" },
    "Hiring Insights":    { bg: "#f0fdf4", color: "#15803d" },
    "Industry Insights":  { bg: "#faf5ff", color: "#7c3aed" },
    "Product Update":     { bg: "#fff7ed", color: "#c2410c" },
  };
  const catStyle = categoryColors[article.category] || categoryColors["Career Advice"];

  return (
    <div style={styles.container}>
      {/* Back button */}
      <button style={styles.backBtn} onClick={onBack}>
        ← Back to feed
      </button>

      {/* Article header */}
      <div style={styles.articleHeader}>
        <span style={{ ...styles.categoryTag, backgroundColor: catStyle.bg, color: catStyle.color }}>
          {article.category}
        </span>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
          <h1 style={styles.articleTitle}>{article.title}</h1>
          <ShareButton
            url={article.canonical_url || `${window.location.origin}/feed?card=${card.id}`}
            title={article.title}
            text={article.title}
            style={{ flexShrink: 0, marginTop: "4px" }}
          />
        </div>
        <span style={styles.articleDate}>
          {new Date(article.created_at).toLocaleDateString("en-US", {
            month: "long", day: "numeric", year: "numeric",
          })}
        </span>
      </div>

      {/* Canonical link */}
      {article.canonical_url && (
        <div style={styles.canonicalRow}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <a href={article.canonical_url} style={styles.canonicalLink} target="_blank" rel="canonical noopener">
            Read on blog →
          </a>
        </div>
      )}

      {/* Article image */}
      {article.image_url && (
        <img src={article.image_url} alt={article.title} style={styles.articleImage} />
      )}

      {/* Article body */}
      <div
        style={styles.articleBody}
        dangerouslySetInnerHTML={{ __html: article.article_body }}
      />

      {/* ── Comments section ── */}
      <div style={styles.commentsSection}>
        <h3 style={styles.commentsTitle}>
          Comments {article.comment_count > 0 && `(${article.comment_count})`}
        </h3>

        {/* Comment composer */}
        <div style={styles.commentComposer}>
          <textarea
            style={styles.commentInput}
            placeholder="Start the conversation..."
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            rows={2}
          />
          <button
            style={{
              ...styles.commentBtn,
              opacity: postingComment || !commentBody.trim() ? 0.5 : 1,
            }}
            onClick={handleAddComment}
            disabled={postingComment || !commentBody.trim()}
          >
            {postingComment ? "Posting..." : "Post Comment"}
          </button>
        </div>

        {/* Comments list */}
        {loadingComments ? (
          <p style={styles.loadingComments}>Loading comments...</p>
        ) : comments.length === 0 ? (
          <div style={styles.emptyComments}>
            <p style={styles.emptyCommentsText}>
              Be the first to start the conversation.
            </p>
          </div>
        ) : (
          <div style={styles.commentsList}>
            {comments.map((comment) => (
              <div key={comment.comment_id || comment.id} style={styles.commentItem}>
                <div style={styles.commentAvatar}>
                  {comment.avatar ? (
                    <img src={comment.avatar} alt="" style={styles.avatarImg} />
                  ) : (
                    <div style={styles.avatarFallback}>
                      {getInitials(comment.author_name)}
                    </div>
                  )}
                </div>
                <div style={styles.commentContent}>
                  <div style={styles.commentHeader}>
                    <span style={styles.commentAuthor}>{comment.author_name}</span>
                    <span style={styles.commentTime}>{formatTime(comment.created_at)}</span>
                  </div>
                  {editingId === (comment.comment_id || comment.id) ? (
                    <div style={styles.editForm}>
                      <textarea
                        style={styles.editTextarea}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        rows={2}
                      />
                      <div style={styles.editActions}>
                        <button
                          style={{ ...styles.editSaveBtn, opacity: savingEdit || !editBody.trim() ? 0.5 : 1 }}
                          onClick={() => handleEditComment(comment.comment_id || comment.id)}
                          disabled={savingEdit || !editBody.trim()}
                        >
                          {savingEdit ? "Saving..." : "Save"}
                        </button>
                        <button style={styles.editCancelBtn} onClick={cancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={styles.commentBody}>{comment.body}</p>
                      {(comment.can_edit || comment.can_delete) && (
                        <div style={styles.commentActions}>
                          {comment.can_edit && (
                            <button style={styles.actionBtn} onClick={() => startEdit(comment)}>
                              Edit
                            </button>
                          )}
                          {comment.can_delete && (
                            <button
                              style={{ ...styles.actionBtn, color: "#dc2626" }}
                              onClick={() => handleDeleteComment(comment.comment_id || comment.id)}
                              disabled={deletingId === (comment.comment_id || comment.id)}
                            >
                              {deletingId === (comment.comment_id || comment.id) ? "Deleting..." : "Delete"}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - date) / (1000 * 60 * 60);
  if (diffHrs < 1) return "Just now";
  if (diffHrs < 24) return `${Math.floor(diffHrs)}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "1d ago";
  return `${diffDays}d ago`;
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    padding: "24px",
  },
  backBtn: {
    background: "none",
    border: "none",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3a6ed4",
    cursor: "pointer",
    padding: "0 0 16px 0",
  },
  articleHeader: {
    marginBottom: "16px",
  },
  categoryTag: {
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 10px",
    borderRadius: "4px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    display: "inline-block",
    marginBottom: "10px",
  },
  articleTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#0f172a",
    margin: "0 0 6px 0",
    lineHeight: 1.2,
  },
  articleDate: {
    fontSize: "13px",
    color: "#94a3b8",
  },
  canonicalRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "16px",
  },
  canonicalLink: {
    fontSize: "13px",
    color: "#3a6ed4",
    textDecoration: "none",
  },
  articleImage: {
    width: "100%",
    maxHeight: "360px",
    objectFit: "cover",
    borderRadius: "8px",
    marginBottom: "20px",
    display: "block",
  },
  articleBody: {
    fontSize: "15px",
    color: "#1e293b",
    lineHeight: 1.7,
    "& p": { marginBottom: "14px" },
    "& h2": { fontSize: "20px", fontWeight: "700", marginTop: "24px", marginBottom: "10px" },
    "& h3": { fontSize: "17px", fontWeight: "700", marginTop: "20px", marginBottom: "8px" },
    "& img": { maxWidth: "100%", borderRadius: "8px", margin: "14px 0" },
    "& a": { color: "#3a6ed4" },
  },
  commentsSection: {
    marginTop: "28px",
    paddingTop: "20px",
    borderTop: "1px solid #f1f5f9",
  },
  commentsTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
    margin: "0 0 14px 0",
  },
  commentComposer: {
    marginBottom: "20px",
  },
  commentInput: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  commentBtn: {
    marginTop: "8px",
    backgroundColor: "#3a6ed4",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  loadingComments: {
    fontSize: "13px",
    color: "#94a3b8",
    textAlign: "center",
    padding: "16px",
  },
  emptyComments: {
    textAlign: "center",
    padding: "24px",
  },
  emptyCommentsText: {
    fontSize: "14px",
    color: "#94a3b8",
  },
  commentsList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  commentItem: {
    display: "flex",
    gap: "10px",
  },
  commentAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarFallback: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundColor: "#e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
  },
  commentContent: {
    flex: 1,
    minWidth: 0,
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "2px",
  },
  commentAuthor: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },
  commentTime: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  commentBody: {
    fontSize: "14px",
    color: "#334155",
    margin: 0,
    lineHeight: 1.5,
  },
  commentActions: {
    display: "flex",
    gap: "12px",
    marginTop: "6px",
  },
  actionBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "0",
  },
  editForm: {
    marginTop: "6px",
  },
  editTextarea: {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "8px 10px",
    fontSize: "14px",
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  editActions: {
    display: "flex",
    gap: "8px",
    marginTop: "6px",
  },
  editSaveBtn: {
    backgroundColor: "#3a6ed4",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  editCancelBtn: {
    backgroundColor: "#f1f5f9",
    color: "#64748b",
    border: "none",
    borderRadius: "6px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  loadingWrap: {
    padding: "40px",
    textAlign: "center",
  },
  spinner: {
    width: "28px",
    height: "28px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#3a6ed4",
    borderRadius: "50%",
    margin: "0 auto 12px",
    animation: "spin 0.8s linear infinite",
  },
  loadingText: {
    fontSize: "14px",
    color: "#94a3b8",
  },
  errorWrap: {
    padding: "40px",
    textAlign: "center",
  },
};

export default EditorialArticleView;
